import { addDays, endOfDay, startOfDay, subMonths } from 'date-fns';
import { IOrdersReceipt } from 'src/types/OrderHistory';
import { transformPenniesToDollar } from 'src/utilities/currency-utils';
import { sdk } from 'src/utilities/sdk';
import {
  removeSpecialCharacters,
  removeWhitespaces,
  toTitleCase,
  truncateString,
} from 'src/utilities/string-utils';
import { getFormattedTimezoneFromObject } from 'src/utilities/time-utils';
import { AsyncTaskStatus } from 'src/utilities/useAsyncTask';

export const timeToWaitUntilErrorCloses = 3000;

export enum PDFGenerationApiStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
  NOT_FOUND = 'NOT_FOUND',
  SUCCESS = 'SUCCESS',
  IDLE = 'IDLE',
}

export enum PDFGenerationStatus {
  IDLE = 'IDLE',
  GENERATE = 'GENERATE',
  FETCHING = 'FETCHING',
  DOWNLOAD = 'DOWNLOAD',
  ERROR = 'ERROR',
}

export const asyncTaskToPDFGenerationStatusMap = new Map<
  AsyncTaskStatus,
  PDFGenerationStatus
>([
  [AsyncTaskStatus.IN_PROGRESS, PDFGenerationStatus.FETCHING],
  [AsyncTaskStatus.COMPLETE, PDFGenerationStatus.DOWNLOAD],
  [AsyncTaskStatus.ERROR, PDFGenerationStatus.ERROR],
  [AsyncTaskStatus.IDLE, PDFGenerationStatus.IDLE],
]);

export const apiPdfStatusToClientPdfStatusMap = new Map<
  PDFGenerationApiStatus,
  PDFGenerationStatus
>([
  [PDFGenerationApiStatus.IN_PROGRESS, PDFGenerationStatus.FETCHING],
  [PDFGenerationApiStatus.COMPLETE, PDFGenerationStatus.DOWNLOAD],
  [PDFGenerationApiStatus.ERROR, PDFGenerationStatus.ERROR],
  [PDFGenerationApiStatus.NOT_FOUND, PDFGenerationStatus.GENERATE],
  [PDFGenerationApiStatus.IDLE, PDFGenerationStatus.IDLE],
]);

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export interface OrderHistoryPageData extends DateRange {
  corpId: string;
}

export const ORDER_ACTIVITY_PAGE_STORAGE_KEY = 'order_activity_page_data';
export const ZIP_FILE_DATE_FORMAT = 'MMddyyyy';

export const getDefaultDateRange = (): DateRange => ({
  startDate: subMonths(startOfDay(new Date()), 6),
  endDate: addDays(endOfDay(new Date()), 14),
});

export const getRestaurantNameForFile = (order: IOrdersReceipt): string => {
  const nameWithoutSpecialCharacters = removeSpecialCharacters(
    order.ordered_from.restaurant_name
  );
  const nameCapitalized = toTitleCase(nameWithoutSpecialCharacters);
  const nameWithoutSpaces = removeWhitespaces(nameCapitalized);

  return truncateString(nameWithoutSpaces, 15);
};

export const getPdfFilename = (order: IOrdersReceipt): string => {
  const date = truncateString(
    getFormattedTimezoneFromObject(
      order.delivered_on_utc,
      order.time_zone,
      'yyyyMMdd'
    ),
    8
  );

  const restaurant = getRestaurantNameForFile(order);
  const name = truncateString(removeWhitespaces(order.ordered_by.name), 20);
  const [dollars, cents] = transformPenniesToDollar(
    order.loc_total + order.transaction_fee
  ).split('.');

  return `${date}_${restaurant}_${name}_${dollars}_${cents}`;
};

export const getDateRangeFromStorage = (corpId: string): DateRange => {
  const pageData: OrderHistoryPageData = sdk.services.storage.get(
    ORDER_ACTIVITY_PAGE_STORAGE_KEY
  );

  if (pageData && pageData.corpId === corpId) {
    return {
      startDate: pageData.startDate ? new Date(pageData.startDate) : null,
      endDate: pageData.endDate ? new Date(pageData.endDate) : null,
    };
  }

  return getDefaultDateRange();
};

export const setDateRangeToStorage = (
  corpId: string,
  { startDate, endDate }: DateRange
): void => {
  sdk.services.storage.set(ORDER_ACTIVITY_PAGE_STORAGE_KEY, {
    corpId,
    startDate,
    endDate,
  });
};
